import { useCallback, useEffect } from 'react';

type ElementRef<T extends HTMLElement> = { current: T | null };

type UseScrollToTopParams<T extends HTMLElement = HTMLElement> = {
  searchInputRef: ElementRef<T>;
  isMobile: boolean;
  topGap?: number;
  onlyWhenInputFocused?: boolean;

  enabled?: boolean;
  alignOnKeyboardOpen?: boolean;
  keyboardThresholdPx?: number;
};

/**
 * Scroll helper for mobile UX (iOS Safari and Android).
 *
 * When the on-screen keyboard opens, the browser may auto-scroll and "center" the focused
 * input. This hook aligns the given input to the top with `topGap` and can re-align when
 * the keyboard is detected (via `visualViewport` height shrink).
 *
 * Uses a single scrollTo (no scrollIntoView) to avoid over-scrolling on Android.
 * Use `enabled` to gate the keyboard-listener lifecycle (e.g. only while an overlay is open).
 */
export const useScrollToTop = <T extends HTMLElement = HTMLElement>({
  searchInputRef,
  isMobile,
  topGap = 12,
  onlyWhenInputFocused = true,

  enabled = true,
  alignOnKeyboardOpen = false,
  keyboardThresholdPx = 80,
}: UseScrollToTopParams<T>) => {
  const scrollToTop = useCallback(() => {
    const currentInput = searchInputRef.current;
    if (!isMobile || !currentInput) {
      return;
    }
    // Single scroll to avoid over-scrolling on Android: scrollIntoView + scrollTo(top - topGap)
    // was effectively scrolling up twice (element to top, then again by topGap).
    const top = currentInput.getBoundingClientRect().top + window.scrollY - topGap;
    const y = Math.max(0, top);
    window.scrollTo({ top: y, behavior: 'auto' });
  }, [isMobile, onlyWhenInputFocused, searchInputRef, topGap]);

  useEffect(() => {
    if (!alignOnKeyboardOpen || !enabled || !isMobile) {
      return;
    }

    const computeKeyboardOpen = () => {
      const vv = window.visualViewport;
      if (!vv) {
        return false;
      }
      const diff = window.innerHeight - vv.height;
      return diff > keyboardThresholdPx;
    };

    let prev = computeKeyboardOpen();

    const update = () => {
      const open = computeKeyboardOpen();
      if (!prev && open) {
        scrollToTop();
      }
      prev = open;
    };

    const vv = window.visualViewport;
    vv?.addEventListener('resize', update);
    window.addEventListener('resize', update);

    return () => {
      vv?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
    };
  }, [alignOnKeyboardOpen, enabled, isMobile, keyboardThresholdPx, scrollToTop]);

  return scrollToTop;
};
